/**
 * Product Card Quick Add
 * Handles adding products to cart directly from:
 * 1. Size buttons on product cards (inline overlay)
 * 2. Bottom drawer triggered by bag icon (narrow cards fallback)
 */
class ProductCardQuickAdd {
  constructor() {
    this.drawer = document.querySelector("quick-add-bottom-drawer");
    this.triggerElement = null;
    this.drawerState = "closed";
    this.isAddingToCart = false;
    this._transitionHandler = null;
    this._transitionTimeout = null;
    this.init();
  }

  init() {
    document.addEventListener("click", this.handleClick.bind(this));

    if (this.drawer) {
      this.drawer
        .querySelector(".quick-add-bottom-drawer__close")
        .addEventListener("click", this.closeDrawer.bind(this));
    }

    // Escape key closes drawer regardless of focus location
    document.addEventListener("keydown", (e) => {
      if (
        e.key === "Escape" &&
        (this.drawerState === "open" || this.drawerState === "opening")
      ) {
        this.closeDrawer();
      }
    });
  }

  handleClick(event) {
    // Handle drawer size button click (only when drawer is fully open)
    const drawerSizeBtn = event.target.closest(
      ".quick-add-bottom-drawer__size-btn"
    );
    if (
      drawerSizeBtn &&
      !drawerSizeBtn.classList.contains("disabled") &&
      this.drawerState === "open"
    ) {
      event.preventDefault();
      this.addToCart(drawerSizeBtn, { closeDrawer: true });
      return;
    }

    // Close drawer on any click outside the panel (if open),
    // but skip if clicking another bag icon (let openDrawer swap content)
    if (this.drawerState === "open" || this.drawerState === "opening") {
      const panel = this.drawer?.querySelector(
        ".quick-add-bottom-drawer__panel"
      );
      const isDrawerTrigger = !!event.target.closest(
        "[data-quick-add-drawer-trigger]"
      );
      if (panel && !panel.contains(event.target) && !isDrawerTrigger) {
        this.closeDrawer();
      }
    }

    // Handle inline size button click
    const sizeBtn = event.target.closest(".product-card__quick-add-btn");
    if (sizeBtn && !sizeBtn.disabled) {
      event.preventDefault();
      this.addToCart(sizeBtn);
      return;
    }

    // Handle bag icon click → open or swap bottom drawer content
    const iconBtn = event.target.closest("[data-quick-add-drawer-trigger]");
    if (iconBtn) {
      event.preventDefault();
      this.openDrawer(iconBtn);
      return;
    }
  }

  // --- Add to cart (unified for inline buttons and drawer) ---

  addToCart(btn, { closeDrawer = false } = {}) {
    const variantId = btn.dataset.variantId;
    if (!variantId || this.isAddingToCart) return;

    btn.classList.add("loading");
    this.isAddingToCart = true;

    this.fetchAddToCart(variantId)
      .then((response) => {
        if (response.status) {
          publish(PUB_SUB_EVENTS.cartError, {
            source: "product-card-quick-add",
            productVariantId: variantId,
            errors: response.errors || response.description
          });
          btn.classList.add("disabled");
          btn.disabled = true;
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: "product-card-quick-add",
          productVariantId: variantId,
          cartData: response
        });

        // Close bottom drawer before opening cart drawer. This ordering is
        // load-bearing: the global trapFocusHandlers singleton requires
        // closeDrawer's removeTrapFocus() to run before the cart drawer
        // calls trapFocus() — otherwise the two focus traps collide.
        if (closeDrawer) this.closeDrawer();

        const cartDrawer = document.querySelector("cart-drawer");
        if (cartDrawer && typeof cartDrawer.renderContents === "function") {
          cartDrawer.renderContents(response);
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      })
      .finally(() => {
        btn.classList.remove("loading");
        this.isAddingToCart = false;
      });
  }

  // --- Bottom drawer logic ---

  openDrawer(triggerBtn) {
    if (!this.drawer) return;

    const card = triggerBtn.closest("product-card, .lookbook-product-card");
    if (!card) return;

    // If drawer is already open, just swap the content
    if (this.drawerState === "open" || this.drawerState === "opening") {
      this.triggerElement = triggerBtn;
      this.populateDrawer(card);
      return;
    }

    if (this.drawerState !== "closed") return;

    this.triggerElement = triggerBtn;
    this.drawerState = "opening";
    this.populateDrawer(card);
    this.drawer.classList.add("is--open");
    document.body.classList.add("overflow-hidden");

    this._onTransitionSettle(() => {
      this.drawerState = "open";
      trapFocus(
        this.drawer.querySelector(".quick-add-bottom-drawer__panel")
      );
    });
  }

  populateDrawer(card) {
    const isLookbookCard = card.classList.contains("lookbook-product-card");

    // Title
    const titleEl = isLookbookCard
      ? card.querySelector(".lookbook-product-card__title")
      : card.querySelector(".product-card__title .product-card__link span");
    const title = titleEl ? titleEl.textContent.trim() : "";
    this.drawer.querySelector(".quick-add-bottom-drawer__title").textContent =
      title;

    // Price — use innerHTML to preserve sale/compare markup
    const priceEl = card.querySelector(
      isLookbookCard ? ".lookbook-product-card__price" : ".product-card__price"
    );
    this.drawer.querySelector(".quick-add-bottom-drawer__price").innerHTML =
      priceEl ? priceEl.innerHTML : "";

    // Image — copy srcset so the browser picks the right resolution
    const imgEl = card.querySelector(
      isLookbookCard
        ? ".lookbook-product-card__media img"
        : ".product-card__media img:not(.hidden):not(.hover-img)"
    );
    const drawerImg = this.drawer.querySelector(
      ".quick-add-bottom-drawer__image img"
    );
    if (imgEl && drawerImg) {
      drawerImg.srcset = imgEl.srcset || "";
      drawerImg.src = imgEl.src;
      drawerImg.sizes = "100px";
      drawerImg.alt = imgEl.alt || title;
    }

    // Size buttons — read from existing quick-add buttons on the card
    const sizesContainer = this.drawer.querySelector(
      ".quick-add-bottom-drawer__sizes"
    );
    sizesContainer.innerHTML = "";

    const cardSizeBtns = card.querySelectorAll(".product-card__quick-add-btn");
    cardSizeBtns.forEach((cardBtn) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "quick-add-bottom-drawer__size-btn";
      btn.dataset.variantId = cardBtn.dataset.variantId;
      btn.textContent = cardBtn.textContent.trim();

      if (cardBtn.disabled || cardBtn.classList.contains("disabled")) {
        btn.classList.add("disabled");
        btn.disabled = true;
      }

      sizesContainer.appendChild(btn);
    });
  }

  closeDrawer() {
    if (
      !this.drawer ||
      (this.drawerState !== "open" && this.drawerState !== "opening")
    )
      return;

    // Cancel any pending open transition before starting close
    this._clearTransitionSettle();

    this.drawerState = "closing";
    this.drawer.classList.remove("is--open");
    document.body.classList.remove("overflow-hidden");

    removeTrapFocus(this.triggerElement);
    this.triggerElement = null;

    this._onTransitionSettle(() => {
      this.drawerState = "closed";
    });
  }

  /**
   * Wait for the panel's transform transition to end, with a safety timeout
   * fallback for cases where transitionend never fires (prefers-reduced-motion,
   * display:none, interrupted transitions).
   */
  _onTransitionSettle(callback) {
    this._clearTransitionSettle();

    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      this._clearTransitionSettle();
      callback();
    };

    const panel = this.drawer.querySelector(".quick-add-bottom-drawer__panel");
    this._transitionHandler = (e) => {
      if (e.propertyName !== "transform") return;
      settle();
    };
    panel.addEventListener("transitionend", this._transitionHandler);

    // Safety net: 500ms = 450ms transition + 50ms grace
    this._transitionTimeout = setTimeout(settle, 500);
  }

  _clearTransitionSettle() {
    if (this._transitionHandler) {
      const panel = this.drawer.querySelector(
        ".quick-add-bottom-drawer__panel"
      );
      panel.removeEventListener("transitionend", this._transitionHandler);
      this._transitionHandler = null;
    }
    if (this._transitionTimeout) {
      clearTimeout(this._transitionTimeout);
      this._transitionTimeout = null;
    }
  }

  // --- Shared fetch helper ---

  fetchAddToCart(variantId) {
    const config = fetchConfig("javascript");
    config.headers["X-Requested-With"] = "XMLHttpRequest";

    const formData = new FormData();
    formData.append("id", variantId);
    formData.append("quantity", 1);

    const drawerSections = document
      .querySelector("cart-drawer")
      ?.getSectionsToRender()
      .map((section) => section.section) ?? ["cart-drawer"];

    formData.append("sections", [...new Set([...drawerSections, "cart-counter"])].join(","));
    formData.append("sections_url", window.location.pathname);

    config.body = formData;
    delete config.headers["Content-Type"];

    return fetch(`${routes.cart_add_url}`, config).then((response) =>
      response.json()
    );
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => {
    new ProductCardQuickAdd();
  });
} else {
  new ProductCardQuickAdd();
}
